import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    Grid,
    TextField,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Pagination,
    Card,
    CardContent,
    Autocomplete
} from '@mui/material';
import {
    AddCircleOutline as AddCircleOutlineIcon,
    VerifiedUser as VerifiedUserIcon,
    AllInclusive as AllInclusiveIcon,
    GetApp as GetAppIcon,
    Refresh as RefreshIcon,
    AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { encryptAppId, encryptText } from '../../utils/encryption';
import * as XLSX from 'xlsx';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

const fetchApplications = async ({ buttonType = 'btnAllApplications', page = 1, limit = 50 }) => {
    const params = new URLSearchParams({ buttonType, page: page.toString(), limit: limit.toString() });
    const response = await fetch(`${API_BASE_URL}/certificate-cell/applications?${params}`, {
        method: 'GET',
        headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
};

const getApplicationTypeName = (assessmentTypeId) => {
    const types = { 1: 'New Application', 2: 'Renewal', 3: 'Extension', 4: 'Surveillance', 5: 'Re-assessment', 6: 'Special', 7: 'Others' };
    return types[assessmentTypeId] || 'Unknown';
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    if (Array.isArray(dateString)) dateString = dateString[0];
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return 'Invalid Date'; }
};

const formatAssessmentDate = (dateString) => {
    if (!dateString) return 'Not Scheduled';
    if (Array.isArray(dateString)) dateString = dateString[0];
    let date;
    if (typeof dateString === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
        const [day, month, year] = dateString.split('/');
        date = new Date(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
    } else {
        date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const calculateDaysPassed = (statusDate) => {
    if (!statusDate) return 'N/A';
    const status = new Date(statusDate);
    const today = new Date();
    status.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return Math.floor((today - status) / (1000 * 60 * 60 * 24));
};

const BUTTON_LABELS = {
    btnAllApplications: 'All Applications',
    btnApplicantLab: 'New Applications',
    btnAccreditatedLab: 'Accredited Applications',
};

const Applications = () => {
    const navigate = useNavigate();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedButtonType, setSelectedButtonType] = useState('btnAllApplications');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [filteredApplications, setFilteredApplications] = useState([]);
    const [exporting, setExporting] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [labIdFilter, setLabIdFilter] = useState('all');
    const [labNameFilter, setLabNameFilter] = useState('all');
    const [stateFilter, setStateFilter] = useState('all');
    const pageSize = 50;

    const loadApplications = useCallback(async (buttonType = selectedButtonType, pageNum = page) => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchApplications({ buttonType, page: pageNum, limit: pageSize });
            if (data.success) {
                setApplications(data.data.applications || []);
                setTotalCount(data.data.totalCount || 0);
                setTotalPages(data.data.totalPages || 1);
                setPage(pageNum);
                setSelectedButtonType(buttonType);
            } else {
                throw new Error(data.message || 'Failed to load applications');
            }
        } catch (err) {
            setError(`Error loading applications: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, [selectedButtonType, page]);

    useEffect(() => { loadApplications(); }, []);

    useEffect(() => {
        let filtered = applications;
        if (statusFilter !== 'all') filtered = filtered.filter(a => (a.status || a.stage) === statusFilter);
        if (labIdFilter !== 'all') filtered = filtered.filter(a => a.applicationNo === labIdFilter);
        if (labNameFilter !== 'all') filtered = filtered.filter(a => a.laboratoryName === labNameFilter);
        if (stateFilter !== 'all') filtered = filtered.filter(a => a.state === stateFilter);
        setFilteredApplications(filtered);
    }, [applications, statusFilter, labIdFilter, labNameFilter, stateFilter]);

    const handleButtonClick = (buttonType) => {
        setPage(1);
        setStatusFilter('all'); setLabIdFilter('all'); setLabNameFilter('all'); setStateFilter('all');
        loadApplications(buttonType, 1);
    };

    const viewApplication = (app) => {
        try {
            const encryptedAppId = encryptAppId(app.appId);
            const encryptedOrgId = encryptText(app.orgId?.toString() || '');
            const encryptedLabTypeId = encryptText(app.labTypeId?.toString() || '');
            const encryptedlaboratoryName = encryptText(app.laboratoryName || '');
            const queryParams = new URLSearchParams({
                appId: encryptedAppId, orgId: encryptedOrgId,
                labTypeId: encryptedLabTypeId, laboratoryName: encryptedlaboratoryName
            });
            navigate(`/main/do/applications/view?${queryParams.toString()}`);
        } catch (err) {
            console.error('Navigate to view application error:', err);
        }
    };

    const getUniqueStatuses = () => [...new Set(applications.map(a => a.status || a.stage).filter(Boolean))].sort();
    const getUniqueLabIds = () => [...new Set(applications.map(a => a.applicationNo).filter(Boolean))].sort();
    const getUniqueLabNames = () => [...new Set(applications.map(a => a.laboratoryName).filter(Boolean))].sort();
    const getUniqueStates = () => [...new Set(applications.map(a => a.state).filter(Boolean))].sort();

    const exportToExcel = async () => {
        setExporting(true);
        try {
            const response = await fetchApplications({ buttonType: selectedButtonType, page: 1, limit: 1000000 });
            if (!response.success) throw new Error(response.message || 'Failed to fetch data');
            let allData = response.data.applications || [];
            if (statusFilter !== 'all') allData = allData.filter(a => (a.status || a.stage) === statusFilter);
            if (labIdFilter !== 'all') allData = allData.filter(a => a.applicationNo === labIdFilter);
            if (labNameFilter !== 'all') allData = allData.filter(a => a.laboratoryName === labNameFilter);
            if (stateFilter !== 'all') allData = allData.filter(a => a.state === stateFilter);
            if (allData.length === 0) { setError('No data to export'); return; }
            const exportData = allData.map((app, i) => ({
                'Sr. No.': i + 1,
                'Lab ID': app.applicationNo || 'N/A',
                'Laboratory Name': app.laboratoryName || 'N/A',
                'Username': app.loginName || 'N/A',
                'Standard': app.standard || 'N/A',
                'Assessment Type': app.assessmentType || getApplicationTypeName(app.assessmentTypeId),
                'Status': app.status || app.stage || 'N/A',
                'Status Date': formatDate(app.certStatusDate),
                'City': app.city || 'N/A',
                'State': app.state || 'N/A',
                'Address': app.address || 'N/A',
                'DO Name': app.doName || 'N/A',
                'Application Date': formatDate(app.applicationDate),
                'Submit Date': formatDate(app.submitDate),
                'Assessment Start': formatAssessmentDate(app.assessmentStart),
            }));
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Applications');
            XLSX.writeFile(wb, `certificate_cell_applications_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (err) {
            setError(`Export failed: ${err.message}`);
        } finally {
            setExporting(false);
        }
    };

    const clearFilters = () => {
        setStatusFilter('all'); setLabIdFilter('all'); setLabNameFilter('all'); setStateFilter('all');
    };

    const hasActiveFilters = statusFilter !== 'all' || labIdFilter !== 'all' || labNameFilter !== 'all' || stateFilter !== 'all';

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', mb: 3 }}>
                All Applications — Certificate Cell
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {Object.entries(BUTTON_LABELS).map(([type, label]) => (
                    <Button
                        key={type}
                        variant={selectedButtonType === type ? 'contained' : 'outlined'}
                        onClick={() => handleButtonClick(type)}
                        startIcon={
                            type === 'btnAllApplications' ? <AllInclusiveIcon /> :
                            type === 'btnApplicantLab' ? <AddCircleOutlineIcon /> :
                            <VerifiedUserIcon />
                        }
                    >
                        {label}
                    </Button>
                ))}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    variant="outlined"
                    onClick={exportToExcel}
                    startIcon={<GetAppIcon />}
                    disabled={loading || exporting || filteredApplications.length === 0}
                >
                    {exporting ? 'Exporting...' : 'Export to Excel'}
                </Button>
                <Button variant="outlined" onClick={() => loadApplications()} startIcon={<RefreshIcon />} disabled={loading}>
                    Refresh
                </Button>
            </Box>

            {/* Filters */}
            {!loading && applications.length > 0 && (
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Autocomplete
                        sx={{ minWidth: 220 }} size="small"
                        options={['All Statuses', ...getUniqueStatuses()]}
                        value={statusFilter === 'all' ? 'All Statuses' : statusFilter}
                        onChange={(_, v) => setStatusFilter(v === 'All Statuses' || !v ? 'all' : v)}
                        renderInput={(params) => <TextField {...params} label="Filter by Status" />}
                        isOptionEqualToValue={(o, v) => o === v} clearOnEscape
                    />
                    <Autocomplete
                        sx={{ minWidth: 220 }} size="small"
                        options={['All Lab IDs', ...getUniqueLabIds()]}
                        value={labIdFilter === 'all' ? 'All Lab IDs' : labIdFilter}
                        onChange={(_, v) => setLabIdFilter(v === 'All Lab IDs' || !v ? 'all' : v)}
                        renderInput={(params) => <TextField {...params} label="Filter by Lab ID" />}
                        isOptionEqualToValue={(o, v) => o === v} clearOnEscape
                    />
                    <Autocomplete
                        sx={{ minWidth: 250 }} size="small"
                        options={['All Laboratories', ...getUniqueLabNames()]}
                        value={labNameFilter === 'all' ? 'All Laboratories' : labNameFilter}
                        onChange={(_, v) => setLabNameFilter(v === 'All Laboratories' || !v ? 'all' : v)}
                        renderInput={(params) => <TextField {...params} label="Filter by Laboratory Name" />}
                        isOptionEqualToValue={(o, v) => o === v} clearOnEscape
                    />
                    <Autocomplete
                        sx={{ minWidth: 180 }} size="small"
                        options={['All States', ...getUniqueStates()]}
                        value={stateFilter === 'all' ? 'All States' : stateFilter}
                        onChange={(_, v) => setStateFilter(v === 'All States' || !v ? 'all' : v)}
                        renderInput={(params) => <TextField {...params} label="Filter by State" />}
                        isOptionEqualToValue={(o, v) => o === v} clearOnEscape
                    />
                    {hasActiveFilters && (
                        <Chip
                            label={`Filtered: ${filteredApplications.length} of ${applications.length}`}
                            color="primary" variant="outlined" onDelete={clearFilters}
                        />
                    )}
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && applications.length > 0 && (
                <>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                            {BUTTON_LABELS[selectedButtonType]}
                            <Chip
                                label={hasActiveFilters ? `${filteredApplications.length} of ${totalCount} records` : `${totalCount} records`}
                                size="small" sx={{ ml: 1 }} color="primary" variant="outlined"
                            />
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Page {page} of {totalPages}
                        </Typography>
                    </Box>

                    <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, overflow: 'auto' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {['Sr. No. / Actions', 'Lab Details', 'Laboratory Name', 'Location Details', 'Status', 'DO Name', 'Status Date', 'Application Submitted on', 'Assessment Start Date', 'Assessment End Date'].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: h === 'Sr. No. / Actions' ? 80 : 120, textAlign: h === 'Sr. No. / Actions' ? 'center' : 'left' }}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredApplications.map((app, index) => {
                                    const days = calculateDaysPassed(app.lastUpdated?.[1] || app.applicationDate);
                                    const isNumeric = typeof days === 'number';
                                    const bgColor = isNumeric ? (days > 2 ? '#ffebee' : days > 1 ? '#fff3e0' : '#e8f5e9') : '#f5f5f5';
                                    const textColor = isNumeric ? (days > 2 ? '#c62828' : days > 1 ? '#ef6c00' : '#2e7d32') : '#757575';

                                    return (
                                        <TableRow key={app.appId} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }, '&:hover': { backgroundColor: '#f0f8ff', cursor: 'pointer' } }}>
                                            <TableCell sx={{ textAlign: 'center', width: 80 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.75rem' }}>
                                                        {(page - 1) * pageSize + index + 1}
                                                    </Typography>
                                                    <Button size="small" variant="contained" color="primary" onClick={() => viewApplication(app)}>
                                                        View
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                                        Lab ID: {app.applicationNo}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                                        Username: {app.loginName || app.contactPerson || 'N/A'}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>Standard:</Typography>
                                                        <Chip label={app.standard || ''} size="small" variant="outlined" color="info" sx={{ height: '20px', fontSize: '0.75rem' }} />
                                                    </Box>
                                                    <Chip
                                                        label={app.assessmentType || getApplicationTypeName(app.assessmentTypeId)}
                                                        size="small"
                                                        color={app.assessmentTypeId === 1 ? 'primary' : 'success'}
                                                        variant="filled"
                                                        sx={{ height: '20px', fontSize: '0.75rem', width: 'fit-content' }}
                                                    />
                                                    <Box sx={{ p: 0.5, borderRadius: 1, backgroundColor: bgColor, display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}>
                                                        <AccessTimeIcon sx={{ fontSize: '1rem', color: textColor }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: textColor }}>
                                                            {isNumeric ? `${days} days` : days}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>{app.laboratoryName}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{app.address || ''}</Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        <Chip label={app.city || 'N/A'} size="small" variant="outlined" color="primary" sx={{ height: '20px', fontSize: '0.7rem' }} />
                                                        <Chip label={app.state || 'N/A'} size="small" variant="filled" color="secondary" sx={{ height: '20px', fontSize: '0.7rem' }} />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={app.status || app.stage || 'N/A'}
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    sx={{ fontSize: '0.75rem' }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{app.doName || 'N/A'}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{formatDate(app.certStatusDate)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{formatDate(app.submitDate || app.applicationDate)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{formatAssessmentDate(app.assessmentStart)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{formatAssessmentDate(app.assessment)}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                            <Pagination count={totalPages} page={page} onChange={(_, p) => loadApplications(selectedButtonType, p)} color="primary" />
                        </Box>
                    )}
                </>
            )}

            {!loading && applications.length === 0 && !error && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary">No applications found</Typography>
                </Paper>
            )}
        </Box>
    );
};

export default Applications;
